import { Button } from '@mui/material';
import classNames from 'classnames';
import { useAppFlags } from 'utils/useAppFlags';
import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { getAuthPersonIdSelector } from '@redux/oauth/oauthSelectors';
import { logoutThunk } from '@redux/oauth/oauthThunks';
import { getActiveOrganizationSelector, getOrganizationByIdSelector, isDTCOrganizationCaregiverSelector } from '@redux/organization/organizationSelectors';
import {
  getPersonByIdSelector,
  getSelectedPatientIdSelector,
  hasAuthUserAdminPermissionSelector,
  isSuperAdminSelector,
  hasManageCareTeamPermissionSelector,
  hasPatientAssignPermissionSelector,
  hasPatientClinicalDataInCareTeamPhiReadPermissionSelector,
  hasTeamMemberAccountsCreatePermissionSelector
} from '@redux/person/personSelector';
import { getPersonOverviewThunk } from '@redux/person/personThunks';
import { ReduxState } from '@redux/types';
import { CAIRNS_LOGO_IMAGE_URL } from 'assets/images/images';
import { DropDownMenu } from 'components/DropDownMenu/DropDownMenu';
import { BellIcon, GearIcon, SignOutIcon } from 'components/Icons';
import OrganizationSelector from 'components/OrganizationSelector/OrganizationSelector';
import { Text } from 'components/Typography';
import { ComingSoonLabel } from 'containers/ComingSoonLabel/ComingSoonLabel';
import { isProductionEnvironment } from 'utils/environment';
import { RouteNames } from 'utils/navigationUtils';
import { getInitials } from 'utils/util';
import { useAppDispatch } from '../../hooks';
import style from './NavTopBar.scss';

type PropsFromRedux = ConnectedProps<typeof connectRedux>;

const NavTopBar: React.FC<PropsFromRedux> = ({
  authPersonId,
  personById,
  selectedPatientId,
  hasTeamMemberAccountsCreatePermission,
  hasManageCareTeamPermission,
  hasPatientClinicalDataInCareTeamPhiReadPermission,
  hasAuthUserAdminPermission,
  isSuperAdmin,
  activeOrganization,
  isDTCOrganizationCaregiver,
  getPersonOverview
}) => {
  const authPerson = personById[authPersonId || ''];
  const patientDashboardPath = generatePath(RouteNames.PATIENT_DASHBOARD, { userId: selectedPatientId ?? authPersonId ?? '' });
  const logoImgUrl = activeOrganization?.logoImageUrl ?? CAIRNS_LOGO_IMAGE_URL;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const flags = useAppFlags();
  const showEngagementPlanTask = flags?.engagementPlanTask ?? false;
  const isAlzheimersStudy = flags?.alzheimersSleepStudy ?? false;

  const personInitials = getInitials(authPerson?.fullName ?? '');

  const isActive = (name: string) => {
    return location.pathname.match(`^/.*${name}/?$`);
  };

  useEffect(() => {
    getPersonOverview({});
  }, [activeOrganization?.id]);

  const onManageClick = () => {
    navigate(RouteNames.MANAGE_ORGANIZATION);
  };

  const onSettingsClick = () => {
    if (isSuperAdmin) {
      // ADMIN users go to organizations page
      navigate(RouteNames.ADMIN_ORGANIZATIONS);
    } else {
      // ORG_ADMIN users go to users & permissions page
      navigate(RouteNames.ADMIN_USERS);
    }
  };

  const onLogOutClick = () => {
    dispatch(logoutThunk());
  };

  return (
    <div className={style.topHeaderContainer}>
      <div className={style.headerContainer}>
        <div className={classNames(style.logoContainer, isDTCOrganizationCaregiver ? style.staticWidth : {})}>
          <a href="/">
            <img
              onError={({ currentTarget }) => {
                currentTarget.onerror = null; // prevents looping
                currentTarget.src = CAIRNS_LOGO_IMAGE_URL;
              }}
              src={logoImgUrl}
              alt="Logo"
              className={style.logoImage}
            />
          </a>
        </div>

        {/* Organization Selector - Only for Super Admins */}
        {isSuperAdmin && <OrganizationSelector />}

        {/* Dashboard, Patients, and Manage */}
        <div className={classNames(style.groupContainer, isDTCOrganizationCaregiver ? style.leftAligned : {})}>
          {hasPatientClinicalDataInCareTeamPhiReadPermission && !isDTCOrganizationCaregiver && (
            <div className={style.dashboardTab}>
              <Button disabled className={style.tab} onClick={() => navigate(patientDashboardPath)}>
                Dashboard
              </Button>
              <ComingSoonLabel containerClassName={style.label} />
            </div>
          )}
          {hasPatientClinicalDataInCareTeamPhiReadPermission && (
            <Button className={classNames(style.tab, isActive('patients') ? style.active : {})} onClick={() => navigate(RouteNames.ALL_PATIENTS)}>
              Patients
            </Button>
          )}
          {hasPatientClinicalDataInCareTeamPhiReadPermission && showEngagementPlanTask && !isAlzheimersStudy && !isDTCOrganizationCaregiver && (
            <Button className={classNames(style.tab, isActive('tasks') ? style.active : {})} onClick={() => navigate(RouteNames.TASKS)}>
              Tasks
            </Button>
          )}
          {hasPatientClinicalDataInCareTeamPhiReadPermission && showEngagementPlanTask && !isAlzheimersStudy && !isDTCOrganizationCaregiver && (
            <Button className={classNames(style.tab, isActive('inbox') ? style.active : {})} onClick={() => navigate(RouteNames.INBOX)}>
              Inbox
            </Button>
          )}
          {!isAlzheimersStudy && (hasAuthUserAdminPermission || isSuperAdmin) && !isDTCOrganizationCaregiver && (
            <Button className={classNames(style.tab, isActive('manage') ? style.active : {})} onClick={onManageClick}>
              Manage
            </Button>
          )}
        </div>
        <div className={style.rightMenuContainer}>
          <div className={style.bellContainer}>
            <BellIcon />
          </div>
          <div>
            <DropDownMenu
              customMenuElement={(onMenuClick) => (
                <button className={style.avatar} onClick={onMenuClick}>
                  <Text variant="md" className={style.personInitials}>
                    {personInitials}
                  </Text>
                </button>
              )}
              menuItems={[
                {
                  disabled: true,
                  customMenuItem: () => (
                    <div className={style.menuHeader}>
                      <div className={style.menuInitialsWrapper}>
                        <div className={style.smallAvatar}>
                          <Text variant="sm" className={style.personInitials}>
                            {personInitials}
                          </Text>
                        </div>
                        <Text variant="md" className={style.menuItemText}>
                          {authPerson?.fullName}
                        </Text>
                      </div>
                      <Text variant="sm" className={style.menuEmailText}>
                        {authPerson?.email!.length > 50 ? authPerson?.email?.slice(0, 50) + '...' : authPerson?.email}
                      </Text>
                    </div>
                  )
                },
                hasAuthUserAdminPermission && {
                  text: 'Settings',
                  onClick: onSettingsClick,
                  icon: GearIcon
                },
                {
                  text: 'Log out',
                  onClick: onLogOutClick,
                  icon: SignOutIcon
                }
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const connectRedux = connect(
  (state: ReduxState) => {
    return {
      authPersonId: getAuthPersonIdSelector(state),
      personById: getPersonByIdSelector(state),
      selectedPatientId: getSelectedPatientIdSelector(state),
      hasTeamMemberAccountsCreatePermission: hasTeamMemberAccountsCreatePermissionSelector(state),
      hasManageCareTeamPermission: hasManageCareTeamPermissionSelector(state),
      hasPatientAssignPermission: hasPatientAssignPermissionSelector(state),
      hasPatientClinicalDataInCareTeamPhiReadPermission: hasPatientClinicalDataInCareTeamPhiReadPermissionSelector(state),
      hasAuthUserAdminPermission: hasAuthUserAdminPermissionSelector(state),
      isSuperAdmin: isSuperAdminSelector(state),
      activeOrganization: getActiveOrganizationSelector(state),
      isDTCOrganizationCaregiver: isDTCOrganizationCaregiverSelector(state)
    };
  },
  (dispatch: Function) => ({
    getPersonOverview: getPersonOverviewThunk
  })
);

export default compose(connectRedux)(NavTopBar) as React.ComponentType;
export { NavTopBar };
